import { Company, CompanyDraft, Invoice } from '@prisma/client'
import { routes as codatRoutes } from 'server/modules/codat-adapter/module-routes'
import { nextApiRoutes as esignaturesAdapterApiRoutes } from 'server/modules/esignatures-adapter/module-routes'
import { addQueryParams } from 'lib/url'
import { AddInvoiceStepId } from 'containers/add-invoice/data/addInvoiceSteps'
import { DashboardAction } from 'containers/dashboard/hooks/useQueryParams'
import { OnboardingStepId } from 'containers/onboarding/data/onBoardingSteps'

export type AddInvoiceOrigin = 'dashboard' | 'invoices'
export type InvoiceId = Invoice['id']
export type InvoiceStatus = Invoice['status']
export type CompanyId = Company['id']
export const routes = {
    onboarding: {
        step: (step: OnboardingStepId) => `/onboarding/${step}`,
        initial: () => `/onboarding`,
        prepareDashboard: () => {
            return '/onboarding/prepare-dashboard'
        },
    },
    invoices: {
        all: ({ invoiceId }: { invoiceId?: InvoiceId } = {}) => {
            return addQueryParams('/invoices', { invoiceId })
        },
        add: {
            step: ({
                step,
                params,
            }: {
                step: AddInvoiceStepId
                params?: {
                    invoiceId?: InvoiceId
                    id?: string
                    from?: AddInvoiceOrigin
                }
            }) => {
                return addQueryParams(`/invoices/add/${step}`, params)
            },
        },
        lendingContract: ({ invoiceId }: { invoiceId: InvoiceId }) =>
            `/invoices/${invoiceId}/lending-contract`,
    },
    dashboard: (
        params?:
            | {
                  action: DashboardAction['requestCredit']
              }
            | {
                  action: DashboardAction['showInvoiceComplete']
                  invoiceId: string
              },
    ) => {
        return addQueryParams('/dashboard', params)
    },
    api: {
        uploadInvoice: () => '/api/invoices/upload',
        // TODO [231]: should we add another handler for failures to log them? not sure
        esignatures: esignaturesAdapterApiRoutes,
    },
    admin: {
        // the route to check you're logged in as an admin
        // could be killed if we have more suitable pages to check that
        home: () => '/admin',
        test: () => '/admin/test',
        login: () => '/admin/login',
        invoices: {
            all: (queryParams?: { companyId?: CompanyId; status?: InvoiceStatus }) =>
                addQueryParams('/admin/invoices', queryParams),
            invoice: ({ invoiceId }: { invoiceId: InvoiceId }) => {
                return `/admin/invoices/${invoiceId}`
            },
            riskAssessment: ({ invoiceId }: { invoiceId: InvoiceId }) =>
                `/admin/invoices/risk-assessment/${invoiceId}`,
        },
        users: {
            all: (queryParams?: { companyId?: CompanyId }) =>
                addQueryParams('/admin/users', queryParams),
        },
        companies: {
            all: () => '/admin/companies',
            onboarding: () => '/admin/companies/onboarding',
            company: ({ companyId }: { companyId: CompanyId }) => {
                return `/admin/companies/${companyId}`
            },
            companyDraft: ({
                companyDraftId,
            }: {
                companyDraftId: CompanyDraft['id']
            }) => {
                return `/admin/companies/onboarding/${companyDraftId}`
            },
        },
        credit: () => '/admin/credit',
        model: () => '/admin/model',
        discounts: () => '/admin/discounts',
        financialHistory: () => '/admin/financial-history',
        availableFunds: () => '/admin/available-funds',
        rates: () => '/admin/rates',
        codat: codatRoutes,
    },
    complianceChecklist: ({ invoiceId }: { invoiceId?: InvoiceId } = {}) => {
        return addQueryParams('/compliance-checklist', { invoiceId })
    },
    home: () => '/',
    privacyPolicy: () => 'https://www.kikin.io/privacy-policy',
    termsOfService: () => 'https://kikin.io/terms-of-service',
    esgProfile: () => '/esg-profile',
    company: () => '/company',
    discounts: () => '/discounts',
    sendFeedback: () => '/send-feedback',
    support: () => '/support',
    checkEmail: (email: string) => addQueryParams('/check-email', { email }),
    pipedream: {
        submitFeedback: () => 'https://18feb9ecf2dd4770d1a2f2287a93b0b7.m.pipedream.net',
        redirectConfig: () => 'https://eovafvnfn7i2ibu.m.pipedream.net',
    },
    govUK: () => 'http://www.gov.uk',
    calculator: () => '/calculator',
}
